import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Home, FileText, Users } from 'lucide-react';

const SignatureConfirmationPage = () => {
  const location = useLocation();
  const { associationName, presidentName } = location.state || {};

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                <span className="text-white font-serif font-bold text-lg">C</span>
              </div>
              <span className="font-serif font-bold text-white text-xl">CCA Montreal</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="glass border-white/10 max-w-lg w-full">
          <CardContent className="p-8 text-center">
            {/* Success Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-cameroon-green/20 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-cameroon-green" />
              </div>
            </div>

            {/* Success Message */}
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
              Signature Submitted Successfully!
            </h1>
            
            <p className="text-white/70 mb-6 leading-relaxed">
              Thank you for signing the CCA Constitution. Your signature has been recorded and is now part of the official document.
            </p>

            {/* Signature Details */}
            {(associationName || presidentName) && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-semibold text-cameroon-yellow uppercase tracking-wide mb-3">
                  Signature Details
                </h3>
                {associationName && (
                  <div className="flex items-center gap-2 text-white/80 mb-2">
                    <Users className="w-4 h-4 text-cameroon-green" />
                    <span className="text-sm">
                      <span className="text-white/50">Association:</span> {associationName}
                    </span>
                  </div>
                )}
                {presidentName && (
                  <div className="flex items-center gap-2 text-white/80">
                    <FileText className="w-4 h-4 text-cameroon-green" />
                    <span className="text-sm">
                      <span className="text-white/50">Signed by:</span> {presidentName}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Next Steps */}
            <div className="border-t border-white/10 pt-6 mb-6">
              <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wide mb-3">
                What's Next?
              </h3>
              <ul className="text-sm text-white/70 space-y-2 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-cameroon-green">•</span>
                  <span>Your signature is now visible on the Constitution page</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cameroon-green">•</span>
                  <span>The admin will be able to download the signed document</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cameroon-green">•</span>
                  <span>You can share this achievement with your association members</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full border-white/20 text-white hover:bg-white/10"
                  data-testid="go-home-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go to Home
                </Button>
              </Link>
              <Link to="/constitution" className="flex-1">
                <Button 
                  className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                  data-testid="view-constitution-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Constitution
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Council of Cultural Associations Montreal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SignatureConfirmationPage;
